import { array, boolean, date, number, object, string } from "yup";
import { produce } from "immer";
import moment from "moment/moment";

export const stringMax = (maxLength) =>
  string().max(maxLength, `${maxLength} caractères maximum`);

export const dateMax = () =>
  date().max(moment("9999-12-31"), "La date est invalide");

/**
 * Date à valider contre un champ frère du même objet, comparée au jour près (le même jour est accepté).
 * Ne valide rien tant que l'une des deux dates est vide.
 */
export const dateSameOrAfter = (otherField, message) =>
  dateMax().test("date-same-or-after", message, function (value) {
    const other = this.parent[otherField];
    return (
      !value || !other || moment(value).isSameOrAfter(moment(other), "day")
    );
  });

export const timeSchema = () => {
  const timeFormat = /^([01]\d|2[0-3]):([0-5]\d)$/;
  return string().matches(timeFormat, "L'heure doit être au format HH:mm");
};

export const numberOrDefault = (def = 0) =>
  number()
    .transform((current) => {
      if (isNaN(current)) {
        return def;
      }
      return current;
    })
    .default(def);

export const integerOrDefault = (def = 0) =>
  numberOrDefault(def).integer().truncate();

export function sanitizeValues(values) {
  try {
    return produce(values, (draft) => {
      Object.keys(draft).forEach((key) => {
        if (typeof draft[key] === "string") {
          draft[key] = draft[key].trim();
        }
        if (draft[key] === "" || draft[key] === null) {
          draft[key] = null;
        } else if (Array.isArray(draft[key])) {
          draft[key].forEach((item, index) => {
            if (typeof item === "object") {
              draft[key][index] = sanitizeValues(item);
            }
          });
        } else if (typeof draft[key] === "object") {
          draft[key] = sanitizeValues(draft[key]);
        }
      });
    });
  } catch (e) {
    console.error("Couldn't sanitize values, returning original", values, e);
    return values;
  }
}

export function toStringSchema(schema) {
  if (schema === undefined) return;
  const type = schema.type;

  if (
    type === "string" ||
    type === "number" ||
    type === "date" ||
    type === "mixed"
  ) {
    return string().nullable().ensure();
  }

  if (type === "boolean") {
    return boolean()
      .nullable()
      .default(false)
      .transform((value) => !!value);
  }

  if (type === "array") {
    return array().of(toStringSchema(schema.innerType)).nullable().ensure();
  }

  if (type === "object") {
    const newSchema = {};
    for (const [f, s] of Object.entries(schema.fields)) {
      newSchema[f] = toStringSchema(s);
    }
    return object(newSchema);
  }
}

export function toInitialValuesFromSchema(schema, toConvert, defaultValue) {
  if (toConvert === null || toConvert === undefined) {
    return toStringSchema(schema).cast(defaultValue);
  }
  return toStringSchema(schema).cast(toConvert);
}

export function toNestedStructure(
  flatlist,
  foreignKey,
  nestedKey,
  primaryKey = "id",
) {
  // Accept either a single (foreignKey, nestedKey) relation or parallel arrays
  // of several relations (e.g. garants + représentants légaux nested under a titulaire).
  const foreignKeys = Array.isArray(foreignKey) ? foreignKey : [foreignKey];
  const nestedKeys = Array.isArray(nestedKey) ? nestedKey : [nestedKey];
  const nestedStruct = {};
  for (let item of flatlist) {
    nestedStruct[item[primaryKey]] = { ...item };
    for (let nk of nestedKeys) {
      nestedStruct[item[primaryKey]][nk] = [];
    }
  }
  for (let item of flatlist) {
    for (let i = 0; i < foreignKeys.length; i++) {
      if (item[foreignKeys[i]]) {
        nestedStruct[item[foreignKeys[i]]][nestedKeys[i]].push(
          nestedStruct[item[primaryKey]],
        );
      }
    }
  }
  for (let item of flatlist) {
    if (foreignKeys.some((fk) => item[fk])) {
      delete nestedStruct[item[primaryKey]];
    }
  }
  return Object.values(nestedStruct);
}

export function upperCaseFirstLetter(word) {
  return word.charAt(0).toUpperCase() + word.slice(1);
}

export class Data {
  static create(data) {
    const instance = new this();
    for (let key of Object.keys(instance)) {
      if (key in data) {
        instance[key] = data[key];
      }
    }
    return instance;
  }
}
